// userService.ts
import { authBus } from "@contexts/AuthContext/AuthEvents";
import { getAuthToken } from "@contexts/AuthContext/functions/getAuthToken";

import { GetUser } from "@services/GACApi4/user/get";
import { setUser, getUser, initUserPersistence, clearUser } from "./gacUserStore";
import { UserOutputType } from "../GACApi4/schemas/user";

const USER_FETCH_LOCK = "gac:user-fetch-lock";
const LOCK_TTL_MS = 10_000;

const USER_LAST_FETCHED_KEY = "gac:user-last-fetched";
const DAILY_REFRESH_MS = 24 * 60 * 60 * 1000;

const shouldRefreshUser = (): boolean => {
  try {
    const raw = localStorage.getItem(USER_LAST_FETCHED_KEY);
    if (!raw) return true;
    return Date.now() - parseInt(raw, 10) > DAILY_REFRESH_MS;
  } catch {
    return true;
  }
};

const markUserFetched = () => {
  try {
    localStorage.setItem(USER_LAST_FETCHED_KEY, String(Date.now()));
  } catch {
    /* empty */
  }
};

let initialized = false;

let gacUserFetchPromise: Promise<UserOutputType | null> | null = null;

// Bumped whenever loadUser() decides the user is logged out. Lets a fetch started
// before that decision detect it's stale and skip writing its (now-outdated) result
// into the store once it resolves, instead of racing with / undoing the clearUser().
let currentRequestId = 0;

// #region  "lock helpers - prevent concurrent fetches across tabs"
const acquireCrossTabLock = (): boolean => {
  const now = Date.now();
  const raw = localStorage.getItem(USER_FETCH_LOCK);

  if (raw) {
    try {
      const { ts } = JSON.parse(raw);
      if (now - ts < LOCK_TTL_MS) {
        return false;
      }
    } catch {
      // corrupted lock, overwrite
    }
  }

  localStorage.setItem(USER_FETCH_LOCK, JSON.stringify({ ts: now }));
  return true;
};

const releaseCrossTabLock = () => {
  localStorage.removeItem(USER_FETCH_LOCK);
};

export const setGacUser = (user: UserOutputType | null) => {
  if (!user) {
    clearUser();
    return;
  }

  setUser(user);
};

export const getGacUser = (): UserOutputType | null => getUser();

const getUserOnce = async (): Promise<UserOutputType | null> => {
  if (gacUserFetchPromise) {
    return gacUserFetchPromise;
  }

  // Someone else (tab) is responsible
  if (!acquireCrossTabLock()) {
    return null;
  }

  const requestId = currentRequestId;

  gacUserFetchPromise = (async () => {
    try {
      const user = await GetUser();
      if (user && requestId === currentRequestId) {
        setGacUser(user);
        markUserFetched();
      }
      return user;
    } catch (err) {
      console.error("Failed to fetch user from API", err);
      if (requestId === currentRequestId) {
        setGacUser(null);
      }
      return null;
    } finally {
      releaseCrossTabLock();
      gacUserFetchPromise = null;
    }
  })();

  return gacUserFetchPromise;
};

export const loadUser = async () => {
  const authtoken = await getAuthToken();

  // couldnt get authToken from cognito, user not logged in, ensure user is clear
  if (!authtoken) {
    // console.log('No auth token found, clearing GAC user');
    currentRequestId += 1; // invalidate any in-flight fetch so it can't overwrite this
    clearUser();
    return;
  }

  if (getUser() && !shouldRefreshUser()) {
    return;
  }

  // if no stored user, fetch from API using valid token
  getUserOnce();
};

/**
 * Initialize the user service: load user, subscribe to AuthBus, and handle cross-tab sync
 */
export async function initUserService() {
  initUserPersistence();

  if (initialized || typeof window === "undefined") return;
  initialized = true;

  await loadUser();

  // Subscribe to login/logout events
  authBus.subscribe(async (event) => {
    if (event.type !== "transition") return;

    switch (event.state) {
      case "authenticated":
        // console.log("GacUserService Received event : authenticated, fetching user");
        try {
          const user = await getUserOnce();
          if (user) {
            const accountAgeMs = Date.now() - new Date(user.createdDate).getTime();

            authBus.emitMessage(accountAgeMs < 15_000 ? `Welcome, ${user.firstName}!` : `Welcome back, ${user.firstName}`, "success");
          }
        } catch (err) {
          console.error("Failed to fetch user after login", err);
        }
        break;

      case "loggedout":
        // console.log("GacUserService Received event:  loggedout");
        // authBus.emitMessage("You have been signed out.", "success");
        clearUser();
        try {
          localStorage.removeItem(USER_LAST_FETCHED_KEY);
        } catch {
          /* empty */
        }
        break;
      default:
        break;
    }
  });
}
